<template>
  <div class="entity-info-flyout">
    <div :class="{'background-haze' : visible }" @click="slideOutInfo()" />
    <div :class="[{'opened': visible }, {'closed-by-caret': closeOut }]"  class="info-body" >
      <div class="tab" @click="slideOutInfo()">
        <div>
          <i class="ddi ddi-noteboard" />
          <span>Notes</span>
        </div>
        <i :class="{'rotate-180': !visible }" class="ddi ddi-double-caret-down" />
      </div>
      <h3>{{ title }}</h3>
      <div class="vitals">
        <div>
          <h4 class="scripty">Who's on this {{ entitySingularName }}?</h4>
          <avatar-gallery
            :people="members"
            :max-num-avatars="10"
            stack-tightly
          />
        </div>
        <div>
          <h4 class="scripty">Which workflow?</h4>
          <el-tooltip v-if="hasWorkflowDescription">
            <template #content>
              <div>
                {{ workflow.description }}
              </div>
            </template>
            <div :class="{'normal-user' : $permissions.isNormalUserOrGuest}" class="workflow-info" @click="goToWorkflowDetail()">
              <i class="ddi ddi-workflows" />
              <span>{{ workflow.name }}</span>
            </div>
          </el-tooltip>
          <div v-else :class="{'normal-user' : $permissions.isNormalUserOrGuest}" class="workflow-info"  @click="goToWorkflowDetail()">
            <i class="ddi ddi-workflows" />
            <span>{{ workflow.name }}</span>
          </div>
        </div>
      </div>
      <div class="note-wrapper">
        <template v-if="!isLoading">
          <i v-if="$permissions.isAdminOrOwner && !showEditor" class="ddi ddi-edit" @click="showEditor=true" />
          <h4 class="scripty">What's this {{ entitySingularName }} for?</h4>
          <div v-if="showEditor" class="editor-wrapper">
            <editor 
              v-model="note"
              autofocus
              fixed-height
              disable-toolbar-stickyness
            />
            <div class="buttons">
              <dd-button type="text" @click="showEditor=false">Cancel</dd-button>
              <dd-button
                :is-loading="isSubmittingNote" 
                :color="ColorAssistant.getEntityColor(entityType)"
                @click="updateNote"
              >Update Note</dd-button>
            </div>
          </div>
          <template v-else>
            <div class="user-inputed-rich-text note">
              <div v-if="hasNote" v-html="note"/>
              <template v-else>
                <div v-if="$permissions.isAdminOrOwner" class="empty-note clickable" @click="showEditor=true" >
                  Click here to add notes on this {{ entitySingularName }}.<br >
                  <strong>(Goals, important links, contact information...)</strong>
                </div>
                <div v-else class="empty-note">
                  There are no notes about this {{ entitySingularName }} yet.
                </div>
              </template>
            </div>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'
import TextAssistant from '@/helpers/text-assistant'

/* Data types */
import { IIDNameAndDescription, IPersonBase } from '@/api/types/response.types'
import { EEntities } from '@/types'

/* API */
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import MailboxesAPI from '@/api/modules/mailboxes.api'

export default defineComponent({
  name: 'EntityInfoFlyout',

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    entityId: {
      type: Number,
      required: true,
    },
    entityType: {
      type: String as () => EEntities,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    workflow: {
      type: Object as () => IIDNameAndDescription,
      required: true,
    },
    members: {
      type: Array as () => IPersonBase[],
      required: true,
    },
  },

  emits: ['update:show'],

  data() {
    return {
      RouteAssistant,
      ColorAssistant,
      isLoading: true,
      isSubmittingNote: false,
      showEditor: false,
      note: '',
      closeOut: false,
    }
  },
  computed: {
    visible: {
      get(): boolean {
        return this.show
      },
      set(newValue: boolean) {
        this.$emit('update:show', newValue)
      },
    },
    drawerWidthPct(): string {
      return '100%'
    },
    isMailbox(): boolean {
      return this.entityType === EEntities.MAILBOXES
    },
    hasNote(): boolean {
      return this.note !== null
    },
    hasWorkflowDescription(): boolean {
      return (
        this.workflow.description !== null &&
        this.workflow.description.trim() !== ''
      )
    },
    entitySingularName(): string {
      return TextAssistant.entitySingularName(this.entityType).toLowerCase()
    },
  },
  watch: {
    visible: {
      async handler(val: boolean) {
        if (val) {
          this.closeOut = false
          this.isLoading = true
          await this.getNote()
          this.isLoading = false
        } else {
          this.showEditor = false
        }
      },
    },
    closeOut: {
      async handler(val: boolean) {
        if (val) {
          this.visible = false
        }
      },
    },
  },

  methods: {
    slideOutInfo() {
      // Only closing sets closeOut, which slides the flyout out. Vue 3 runs the closeOut watcher before the visible
      // watcher that used to clear it on opening, so setting it then too closed the flyout as soon as it opened
      if (this.visible) {
        this.closeOut = true
      } else {
        this.visible = true
      }
    },
    async getNote() {
      try {
        let response
        if (this.isMailbox) {
          response = await MailboxesAPI.get_Note(this.entityId)
        } else {
          response = await InternalProjectsAPI.get_Note(this.entityId)
        }
        this.note = response.description
      } catch (error) {
        this.$error.emit(error)
      }
    },
    goToWorkflowDetail() {
      if (this.$permissions.isAdminOrOwner) {
        RouteAssistant.goToWorkflowDetail(
          this.$router,
          this.$route.params.accountId,
          this.workflow.id,
        )
      }
    },
    async updateNote() {
      try {
        if (this.isMailbox) {
          await MailboxesAPI.put_Note(this.entityId, this.note)
        } else {
          await InternalProjectsAPI.put_Note(this.entityId, this.note)
        }
        this.showEditor = false
        setTimeout(() => (this.closeOut = true), 300)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },
    handleCancel() {
      this.$emit('update:show', false)
    },
  },
})
</script>

<style lang="scss">
.entity-info-flyout {
  $drawer-height: 680px;
  $drawer-width: 680px;
  $z-index-for-flyout: 1050;

  @keyframes info-body--slide-in {
    0% {
      bottom: -1 * $drawer-height;
    }
    100% {
      bottom: 0;
    }
  }

  @keyframes info-body--slide-out {
    0% {
      bottom: 0;
    }
    100% {
      bottom: -1 * $drawer-height;
    }
  }

  .background-haze {
    position: fixed;
    width: 100vw;
    height: 100vh;
    top: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.1);
    z-index: $z-index-for-flyout;
    backdrop-filter: blur(1px) grayscale(50%);
  }

  .info-body {
    z-index: $z-index-for-flyout;
    position: fixed;
    bottom: -1 * $drawer-height;
    right: 80px;
    background-color: var(--color-background-header);
    display: flex;
    flex-direction: column;
    align-items: center;
    height: $drawer-height;
    width: $drawer-width;
    padding: 20px 30px 40px 30px;
    border: $border--solid-light;
    border-bottom: none;
    border-top-left-radius: 8px;
    @include standard-box-shadow;

    &.opened {
      animation: info-body--slide-in 0.5s forwards;
      animation-timing-function: ease-in-out;
    }

    &.closed-by-caret {
      animation: info-body--slide-out 0.5s forwards;
      animation-timing-function: ease-in-out;
    }

    > .tab {
      position: absolute;
      top: -40px;
      right: -1px;
      background-color: var(--color-background-header);
      width: 140px;
      height: 40px;
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
      border: $border--solid-light;
      padding: 0 10px;
      border-bottom: none;
      display: flex;
      align-items: center;
      justify-content: space-between;
      @include clickable-gray-color-states;

      > div {
        display: flex;
        align-items: center;
        @include flex-row-horizontal-spacing(4px);
        @include lettering--p-sm;
        font-weight: $bold;

        > .ddi-noteboard {
          color: var(--color-text-third);
          top: -1px;
        }
      }

      &:hover {
        border-color: $color--gray-45;
      }

      > .ddi-double-caret-down {
        font-size: 14px;
      }
    }

    h3 {
      margin: 16px 0 0 0;
      text-align: center;
      line-height: 1.2em;
    }

    .scripty {
      font-size: 24px !important;
      color: var(--color-text-primary);
      line-height: 1;
      margin-bottom: 12px;
    }

    .vitals {
      display: flex;
      justify-content: center;
      margin-top: 20px;
      width: 100%;
      @include flex-row-horizontal-spacing(30px);

      > div {
        display: flex;
        align-items: center;
        flex-direction: column;

        .workflow-info {
          display: flex;
          align-items: center;
          cursor: pointer;
          border: $border--solid-light;
          padding: 2px 10px;
          border-radius: 20px;
          background-color: var(--color-background-secondary);
          max-width: 250px;
          @include flex-row-horizontal-spacing(8px);

          &.normal-user {
            cursor: default;
          }

          .ddi {
            color: var(--color-text-third);
          }

          span {
            @include lettering--p-sm;
            @include single-line-ellipsis();
            font-weight: $bold;
          }

          &:hover {
            background-color: $color--gray-30;
          }
        }
      }
    }

    .note-wrapper {
      width: 100%;
      margin: 30px 0 20px 0;
      height: $drawer-height - 240px;
      position: relative;

      .editor-wrapper {
        > div.buttons {
          margin-top: 20px;
          display: flex;
          justify-content: flex-end;
        }
      }

      .ddi-edit {
        position: absolute;
        top: 4px;
        right: 0;

        @include clickable-gray-color-states;
      }

      .note {
        height: 100%;
        overflow-y: auto;
        border-top: $border--dotted-medium;
        padding-top: 20px;

        .empty-note {
          width: 100%;
          padding: 40px 0;
          border: dashed 2px $color--gray-30;
          border-radius: 8px;
          @include lettering--p;
          text-align: center;

          &.clickable {
            cursor: pointer;
          }
        }
      }
    }
  }
}
</style>
